import type { SalesOrderStatus } from '../../../types';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

/**
 * Stock gates run before the Products policy: shipped products lock picked stock
 * and deletion; a receiving lot is refused when mid-receiving shipping is off.
 * `unitsPicked` is units delivered and `lot` is the row's `lotId` write.
 */
export type SalesOrderLineItemStockFacts = {
  salesOrderStatus: SalesOrderStatus;
  /** The row ships a vendor's units (`external`), not picked stock. */
  isExternal: boolean;
  /** The lot being set is MID_RECEIVING and the organization's AllowMidReceivingShipping flag is off. */
  newLotStillReceiving: boolean;
};

export const SALES_ORDER_LINE_ITEM_STOCK_FIELDS = ['unitsPicked', 'lot'] as const;
export type SalesOrderLineItemStockField = (typeof SALES_ORDER_LINE_ITEM_STOCK_FIELDS)[number];

const SHIPPED_STATUSES: readonly SalesOrderStatus[] = ['SHIPPED', 'CLOSED'];

/** Shared fact predicate: the order has shipped, so picked stock is final. */
export const isSalesOrderShipped = (status: SalesOrderStatus): boolean => SHIPPED_STATUSES.includes(status);

const decideField = (facts: SalesOrderLineItemStockFacts, field: SalesOrderLineItemStockField): Decision => {
  if (field === 'lot' && facts.newLotStillReceiving) {
    return deny('SALES_ORDER_LINE_ITEM_LOT_STILL_RECEIVING', 'This lot is still being received.');
  }

  if (facts.isExternal || !isSalesOrderShipped(facts.salesOrderStatus)) return allow();

  return field === 'unitsPicked'
    ? deny('SALES_ORDER_LINE_ITEM_SHIPPED_LOCKED', "Shipped products can't be changed.")
    : deny('SALES_ORDER_LINE_ITEM_SHIPPED_LOCKED', "Shipped products can't be changed.");
};

const fieldRule = (
  facts: SalesOrderLineItemStockFacts,
  action: Action<SalesOrderLineItemStockField, never>
): Decision => (action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow());

export const salesOrderLineItemStockPolicy: Policy<SalesOrderLineItemStockFacts, SalesOrderLineItemStockField, never> =
  definePolicy<SalesOrderLineItemStockFacts, SalesOrderLineItemStockField, never>({
    kind: 'sales order product stock',
    fields: SALES_ORDER_LINE_ITEM_STOCK_FIELDS,
    ops: [],
    requires: {},
    entity: (facts, action) =>
      action.type === 'delete' && isSalesOrderShipped(facts.salesOrderStatus)
        ? deny('SALES_ORDER_LINE_ITEM_SHIPPED_LOCKED', "Shipped products can't be deleted.")
        : allow(),
    field: { unitsPicked: fieldRule, lot: fieldRule },
  });

/**
 * ONE wording for the lot receiving floor — "you cannot receive less than the lot has already
 * used" — on every surface that judges it: the api update door (`lineItem.manager.ts`, Sales and
 * Purchase Order lines), the browser's line-item validator and the Grower Product grid. The api's
 * sentence wins (D3); exported so the Grower Product api door (`workOrderLineItem.manager.ts`,
 * lane L7) adopts the same words. Kept verbatim (no trailing period): this is a quantity
 * validation, not a policy denial, so it never rides the policy transport.
 */
export const lotReceivingFloorMessage = (used: number, unit: 'units' | 'lbs'): string =>
  `The lot on this line item has already used ${used} ${unit}, you cannot receive less than that`;
